import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import MobiusPage from "@insite/mobius/Page";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { generateQueryString } from "@WhiteCap/Common/Utillities/utils";
import getQuoteList from "@WhiteCap/Handlers/OsnQuotes/GetQuoteList";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const queryObj = parseQueryString<{
        quoteNumber?: string;
        createdStartDate?: string;
        createdEndDate?: string;
        expiredStartDate?: string;
        expiredEndDate?: string;
        jobsite?: string;
        branchContact?: string;
    }>(location.search);
    const queryString = generateQueryString(queryObj);
    const session = state.context.session;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        pagination: {
            pageNumber: state.WhiteCap.osnQuotes.quoteData.pageNumber,
            pageSize: state.WhiteCap.osnQuotes.quoteData.pageSize,
            pageSizeOptions: [8, 16, 24, 32],
            totalItemCount: state.WhiteCap.osnQuotes.quoteData.totalQuotes,
        },
        session,
        queryString,
        queryObj,
        billTosById: state.data.billTos.byId,
        userName: state.context.session?.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location,
        isAuthenticated,
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("AccountManager") !== -1,
    };
};

const mapDispatchToProps = {
    getQuoteList,
};

interface QuoteListPageStyles {
    errorMessage?: TypographyProps;
}

const QuoteListPageStyles: QuoteListPageStyles = {
    errorMessage: {
        variant: "h2",
        weight: 500,
        css: css`
            margin-top: 40px;
            text-align: center;
        `,
    },
};

const styles = QuoteListPageStyles;

const OsnQuoteListPage: React.FC<Props> = ({
    id,
    pagination,
    getQuoteList,
    session,
    queryString,
    queryObj,
    billTosById,
    signInPageUrl,
    location,
    isAuthenticated,
    canViewQuotes,
}) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();

    const billToId = session.billToId;
    const billToDetails = billToId ? billTosById[billToId] : "";
    const returnUrl = location.pathname + location.search;
    const notAuthorizedMessage = siteMessage("OSN_Quotes_Module_User_Not_Authorized_Message");

    useEffect(() => {
        if (!billToDetails) {
            return;
        }
        getQuoteList({
            pageNumber: 1,
            pageSize: pagination.pageSize,
            customerNumber: billToDetails?.customerNumber || "",
            ...queryObj,
            onError(message: string) {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
        // Update Filters on UI
        updateFilters();
    }, [queryString, billToDetails]);

    const updateFilters = () => {
        dispatch({
            type: "Custom/OsnQuoteList/UpdateFilters",
            filtersObj: {
                ...queryObj,
            },
        });
    };

    if (!isAuthenticated && !isInShell && signInPageUrl) {
        history.push(`${signInPageUrl}?returnUrl=${returnUrl}`);
        return null;
    }

    return (
        <MobiusPage>
            {canViewQuotes ? (
                <Zone contentId={id} zoneName="Content" requireRows />
            ) : (
                <Typography {...styles.errorMessage}>{notAuthorizedMessage}</Typography>
            )}
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OsnQuoteListPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
